import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { X, Save, Loader, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Screen, Promotion } from '../../../shared/types';
import { bannerService, AlterarTelaBannerCommand, ProdutoBannerOtimizado, TelaBannerOtimizada } from '../../banners/services/bannerService';
import { LAYOUT_TO_TIPO, TIPO_TO_LAYOUT, extrairDescricaoDoJson } from '../../banners/services/bannerSyncService';
import { priceService } from '../../promotions/services/priceService';
import { useStore } from '../../../store/useStore';
import { LayoutType } from '../../../shared/types';
import { layouts } from '../../../store/useStore';

interface ScreenSelectorModalProps {
    screens: Screen[];
    promotions: Promotion[];
    singlePromoId: number | null;
    selectedPromoIds: number[];
    onSelect: (screenId: number) => void;
    onClose: () => void;
    onSaveToAPI?: (linkedProductIds?: number[], unlinkedProductIds?: number[], screensModified?: Map<number, number[]>) => void; // Callback após salvar na API com IDs vinculados/desvinculados e telas modificadas (screenId -> productIds[])
    mode?: 'link' | 'unlink'; // Modo do modal: 'link' para vincular, 'unlink' para desvincular
    telasOtimizadasProp?: TelaBannerOtimizada[]; // Telas já carregadas (opcional) - evita requisição duplicada
    loadingTelasProp?: boolean; // Estado de loading da página pai (opcional) - para mostrar loading imediatamente ao abrir o modal
}

export const ScreenSelectorModal: React.FC<ScreenSelectorModalProps> = ({
    screens,
    promotions,
    singlePromoId,
    selectedPromoIds,
    onSelect,
    onClose,
    onSaveToAPI,
    mode = 'link', // Default é 'link' para manter compatibilidade
    telasOtimizadasProp, // Telas já carregadas (opcional)
    loadingTelasProp = false // Estado de loading da página pai (opcional)
}) => {
    // Estado local para telas otimizadas - usar prop se fornecido, senão carregar
    const [telasOtimizadas, setTelasOtimizadas] = useState<TelaBannerOtimizada[]>(telasOtimizadasProp || []);
    const [loadingTelas, setLoadingTelas] = useState(false);
    const [errorTelas, setErrorTelas] = useState<string | null>(null);
    
    // Ref para evitar múltiplas chamadas durante o ciclo de vida do componente
    const carregouTelasRef = useRef(!!telasOtimizadasProp); // Se recebeu prop, já está "carregado"
    
    // Função para carregar telas COM produtos (Produtos=S)
    const carregarTelas = useCallback(async () => {
        // Evitar múltiplas chamadas simultâneas
        if (loadingTelas || carregouTelasRef.current) {
            return;
        }
        
        carregouTelasRef.current = true;
        setLoadingTelas(true);
        setErrorTelas(null);
        try {
            const telas = await bannerService.listarTelasOtimizadoComProdutos();
            setTelasOtimizadas(telas);
        } catch (err) {
            console.error('Erro ao carregar telas:', err);
            setErrorTelas(err instanceof Error ? err.message : 'Erro ao carregar telas');
            carregouTelasRef.current = false; // Permitir tentar novamente em caso de erro
        } finally {
            setLoadingTelas(false);
        }
    }, [loadingTelas]);
    
    // Atualizar telas quando a prop mudar (se fornecida)
    useEffect(() => {
        if (telasOtimizadasProp && telasOtimizadasProp.length > 0) {
            setTelasOtimizadas(telasOtimizadasProp);
            carregouTelasRef.current = true; // Marcar como carregado
        } else if (telasOtimizadasProp !== undefined && telasOtimizadasProp.length === 0) {
            // Se a prop foi fornecida mas está vazia, significa que a página pai já tentou carregar
            // mas não há telas. Não precisamos carregar novamente.
            carregouTelasRef.current = true;
        }
    }, [telasOtimizadasProp]);
    
    // Carregar telas apenas se não foram fornecidas via prop e ainda não foram carregadas
    useEffect(() => {
        // Se a prop foi fornecida (mesmo que vazia), não carregar - a página pai já cuidou disso
        if (telasOtimizadasProp !== undefined) {
            return;
        }
        
        // Se já carregou ou está carregando, não fazer nada
        if (carregouTelasRef.current || loadingTelas) {
            return;
        }
        
        // Se já tem telas no estado, não precisa carregar
        if (telasOtimizadas.length > 0) {
            return;
        }
        
        // Carregar telas apenas uma vez (quando a prop não foi fornecida)
        carregarTelas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Array vazio = executa apenas uma vez ao montar
    const { promotions: storePromotions, addPromotion } = useStore();
    const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [expandedScreens, setExpandedScreens] = useState<Set<number>>(new Set());
    // Estado para produtos carregados por tela (lazy loading)
    const [loadedProducts, setLoadedProducts] = useState<Map<number, ProdutoBannerOtimizado[]>>(new Map());
    const [loadingProducts, setLoadingProducts] = useState<Map<number, boolean>>(new Map());
    // Estado para controlar expansão do dropdown de produtos que podem ser vinculados
    const [expandedAvailableProducts, setExpandedAvailableProducts] = useState<Set<number>>(new Set());
    // Estado local para rastrear quais produtos foram marcados/desmarcados em cada tela
    // Map<screenId, Set<productId>> - produtos marcados nesta sessão
    const [localSelections, setLocalSelections] = useState<Map<number, Set<number>>>(new Map());
    // Estado para forçar recálculo do hasChangesForScreen quando localSelections mudar
    const [localSelectionsVersion, setLocalSelectionsVersion] = useState(0);
    // Estado para rastrear telas que têm erro de validação
    const [validationErrors, setValidationErrors] = useState<Map<number, string>>(new Map());
    const promoIds = singlePromoId !== null ? [singlePromoId] : selectedPromoIds;
    
    // Converter telas otimizadas da API para formato Screen para usar no modal
    // OTIMIZADO: Usa dados otimizados (sem produtos completos), apenas IDs
    // IMPORTANTE: Filtrar telas do tipo "video" (tipo 2) - elas não devem aparecer para vincular produtos
    const screensFromAPI = useMemo(() => {
        // Se está carregando e não há telas ainda, retornar vazio (evitar processamento desnecessário)
        if ((loadingTelasProp && (!telasOtimizadasProp || telasOtimizadasProp.length === 0)) || (loadingTelas && telasOtimizadas.length === 0)) {
            return [];
        }
        
        // Filtrar telas do tipo "video" (tipo 2) primeiro
        const telasFiltradas = telasOtimizadas.filter(tela => tela.tipo !== 2);
        const telasLength = telasFiltradas.length;
        if (telasLength === 0) {
            return [];
        }
        
        // Criar Map do screens para busca O(1) ao invés de find() O(n)
        const screensMap = new Map<number, Screen>();
        const screensLength = screens.length;
        for (let s = 0; s < screensLength; s++) {
            const screen = screens[s];
            if (screen.id) {
                screensMap.set(screen.id, screen);
            }
        }
        
        // Pré-alocar array com tamanho fixo para melhor performance
        const result: Screen[] = new Array(telasLength);
        
        // Cache de valores padrão para evitar recriação
        const defaultDate = new Date().toISOString();
        const defaultBgColor = '#ffffff';
        const defaultLayoutId = 'simple2col' as LayoutType;
        const defaultDuration = 30;
        const defaultName = 'Tela sem nome';
        
        for (let i = 0; i < telasLength; i++) {
            const tela = telasFiltradas[i];
            const screenLocal = screensMap.get(tela.id);
            
            // Converter tipo para layoutId
            const layoutId = tela.tipo !== undefined 
                ? (TIPO_TO_LAYOUT[tela.tipo] || defaultLayoutId) as LayoutType
                : (screenLocal?.layoutId || defaultLayoutId);
            
            // IMPORTANTE: Extrair descrição do JSON de configurações se não estiver no campo descricao
            let description = tela.descricao || screenLocal?.description || '';
            if (!description && tela.configuracoes) {
                const descricaoDoJson = extrairDescricaoDoJson(tela.configuracoes);
                if (descricaoDoJson) {
                    description = descricaoDoJson;
                }
            }
            
            result[i] = {
                id: tela.id,
                name: tela.nome || screenLocal?.name || defaultName,
                description: description,
                layoutId: layoutId,
                // Usar apenas IDs dos produtos (não produtos completos) - lazy loading
                promotions: tela.produtoIds || [],
                linkedProducts: screenLocal?.linkedProducts || [],
                isActive: tela.ativo ?? screenLocal?.isActive ?? false,
                createdAt: screenLocal?.createdAt || defaultDate,
                backgroundColor: screenLocal?.backgroundColor || defaultBgColor,
                backgroundImage: screenLocal?.backgroundImage || '',
                showHeader: screenLocal?.showHeader ?? true,
                duration: screenLocal?.duration || defaultDuration,
                layoutStyle: screenLocal?.layoutStyle
            } as Screen;
        }
        
        return result;
    }, [telasOtimizadas, screens, loadingTelasProp, telasOtimizadasProp, loadingTelas]);
    
    // Usar telas da API - se não houver telas na API ainda, usar do store como fallback
    // IMPORTANTE: Filtrar telas do tipo "video" também do store local
    // Usar useMemo para estabilizar a referência e evitar re-renderizações desnecessárias
    const screensToUse = useMemo(() => {
        const screensFromStore = screens.filter(s => s.layoutId !== 'video');
        return screensFromAPI.length > 0 ? screensFromAPI : screensFromStore;
    }, [screensFromAPI, screens]);
    
    // Criar uma string estável para identificar as telas (baseada nos IDs) para usar como dependência
    const screensIdsKey = useMemo(() => {
        return screensToUse.map(s => s.id).sort().join(',');
    }, [screensToUse]);
    
    // Buscar produtos que não estão no store quando o modal abre
    // IMPORTANTE: Esta busca é feita de forma assíncrona e não bloqueia o carregamento das telas
    useEffect(() => {
        if (promoIds.length === 0) return;
        
        // Combinar produtos do store e das props
        const allPromotions = [...storePromotions, ...promotions];
        const promotionsMap = new Map(allPromotions.map(p => [p.id, p]));
        
        // Verificar quais produtos não estão no store nem nas props
        const missingIds = promoIds.filter(id => !promotionsMap.has(id));
        
        if (missingIds.length > 0) {
            // Buscar produtos da API de forma assíncrona (não bloqueia UI)
            // Limitado a 10 produtos para não demorar muito
            const idsToFetch = missingIds.slice(0, 10);
            priceService.buscarProdutosPorIds(idsToFetch)
                .then(items => {
                    // Processar e adicionar ao store
                    const today = new Date();
                    const nextWeek = new Date(today);
                    nextWeek.setDate(today.getDate() + 7);
                    const validUntil = nextWeek.toISOString().split('T')[0];
                    
                    items.forEach((item: any) => {
                        const preco = Number(item.vlPrecoNormal || item.preco || 0);
                        const precoPromocional = Number(item.vlPreco || item.precoPromocional || 0);
                        const emPromocao = item.promocao === true || item.promocao === 'true' ||
                            item.emPromocao === true || item.emPromocao === 'true';
                        const nome = item.nome || item.descricao || `Produto ${item.produtoId}`;
                        
                        const promoData: any = {
                            name: nome,
                            price: emPromocao && precoPromocional > 0 ? precoPromocional : (preco > 0 ? preco : 0),
                            originalPrice: emPromocao && preco > 0 ? preco : undefined,
                            unit: item.tipoUnidade || item.unidade || 'UN',
                            category: item.campanha || 'Geral',
                            validUntil: validUntil,
                            description: `Código: ${item.produtoId}${emPromocao ? ' - EM PROMOÇÃO' : ''}`,
                            isVideoPromotion: false,
                            hasAudio: true,
                            autoplay: true,
                            loop: true,
                            createdAt: new Date().toISOString()
                        };
                        
                        addPromotion(promoData, item.produtoId);
                    });
                })
                .catch(() => {
                    // Erro silencioso - não bloquear UI
                });
            // Removido setLoadingMissingProducts - não bloqueia UI
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoIds.join(',')]);

    // Inicializar estado local baseado nos produtos que estão sendo vinculados
    // Usar screensIdsKey e promoIds.join(',') como dependências para evitar loops
    useEffect(() => {
        // Se está carregando, não processar nada ainda
        if ((loadingTelasProp && (!telasOtimizadasProp || telasOtimizadasProp.length === 0)) || (loadingTelas && telasOtimizadas.length === 0)) {
            return;
        }
        
        if (promoIds.length === 0 || screensToUse.length === 0) {
            // Se não há promoIds ou telas, limpar seleções locais
            if (localSelections.size > 0) {
                setLocalSelections(new Map());
            }
            return;
        }
        
        // Só inicializar se ainda não foi inicializado (evitar sobrescrever mudanças do usuário)
        // Verificar se já existe alguma seleção local - se sim, não reinicializar
        if (localSelections.size > 0) {
            return;
        }
        
        // No modo unlink, não inicializar com produtos marcados - usuário escolhe
        if (mode === 'unlink') {
            return;
        }
        
        const initialSelections = new Map<number, Set<number>>();
        screensToUse.forEach(screen => {
            // IMPORTANTE: Usar dados otimizados diretamente da API (telasOtimizadas) para garantir dados atualizados
            const tela = telasOtimizadas.find(t => t.id === screen.id);
            const produtosNaAPI = new Set<number>();
            
            if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                tela.produtoIds.forEach(produtoId => {
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        produtosNaAPI.add(produtoId);
                    }
                });
            }
            
            // Inicializar com produtos que estão na API E nos promoIds (já vinculados)
            const initialSet = new Set<number>();
            promoIds.forEach(id => {
                if (produtosNaAPI.has(id)) {
                    initialSet.add(id);
                }
            });
            
            if (initialSet.size > 0) {
                initialSelections.set(screen.id, initialSet);
            }
        });
        setLocalSelections(initialSelections);
    }, [screensIdsKey, promoIds.join(','), telasOtimizadas, mode]);

    // Função para formatar preço no padrão brasileiro
    const formatPrice = (value: number): string => {
        return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'currency',
            currency: 'BRL'
        });
    };

    // Função para carregar produtos de uma tela quando expandir (lazy loading)
    const loadScreenProducts = useCallback(async (screenId: number) => {
        // Se já está carregando ou já foi carregado, não fazer nada
        if (loadingProducts.get(screenId) || loadedProducts.has(screenId)) {
            return;
        }

        setLoadingProducts(prev => {
            const newMap = new Map(prev);
            newMap.set(screenId, true);
            return newMap;
        });

        try {
            // OTIMIZAÇÃO: Buscar produtos usando Produtos=S no endpoint
            const produtos = await bannerService.buscarProdutosTelaOtimizadoComParametro(screenId);
            
            setLoadedProducts(prev => {
                const newMap = new Map(prev);
                newMap.set(screenId, produtos);
                return newMap;
            });
        } catch (error) {
            // Erro silencioso - não bloquear UI
            console.error(`Erro ao carregar produtos da tela ${screenId}:`, error);
        } finally {
            setLoadingProducts(prev => {
                const newMap = new Map(prev);
                newMap.delete(screenId);
                return newMap;
            });
        }
    }, [loadingProducts, loadedProducts]);

    // Obter produtos vinculados a uma tela - usar produtos carregados ou IDs
    const getScreenProducts = useCallback((screen: Screen): Promotion[] => {
        const screenId = screen.id;
        
        // Se produtos já foram carregados, converter para formato Promotion
        const produtosCarregados = loadedProducts.get(screenId);
        if (produtosCarregados && produtosCarregados.length > 0) {
            // Combinar produtos do store e das props para ter acesso a todos
            const allPromotions = [...storePromotions, ...promotions];
            const promotionsMap = new Map(allPromotions.map(p => [p.id, p]));
            
            // Converter produtos da API para formato Promotion
            return produtosCarregados.map(produto => {
                // Tentar encontrar no store primeiro
                const promotion = promotionsMap.get(produto.produtoId);
                if (promotion) {
                    return promotion;
                }
                
                // Se não encontrou, criar Promotion básico dos dados da API
                return {
                    id: produto.produtoId,
                    name: produto.nome || `Produto ${produto.produtoId}`,
                    price: produto.vlPreco || 0,
                    originalPrice: produto.vlPrecoNormal || produto.vlPreco || 0,
                    unit: produto.tipoUnidade || 'UN',
                    image: '',
                    video: '',
                    description: '',
                    category: '',
                    validUntil: '',
                    isVideoPromotion: false,
                    hasAudio: false,
                    autoplay: false,
                    loop: false,
                    createdAt: new Date().toISOString()
                } as Promotion;
            });
        }
        
        // Se não foram carregados ainda, retornar array vazio (será carregado quando expandir)
        return [];
    }, [loadedProducts, storePromotions, promotions]);

    // Obter quantidade de produtos vinculados a uma tela (baseado nos IDs)
    const getScreenProductsCount = useCallback((screen: Screen): number => {
        // Usar os IDs dos produtos da tela otimizada
        return screen.promotions?.length || 0;
    }, []);

    // Verificar se um produto está marcado para uma tela específica
    const isProductSelectedForScreen = useCallback((screenId: number, productId: number): boolean => {
        // Só verificar para produtos que estão nos promoIds (produtos sendo vinculados)
        if (!promoIds.includes(productId)) {
            return false;
        }
        
        // Buscar tela otimizada
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        // Verificar se está na seleção local (mudanças desta sessão)
        const localSelection = localSelections.get(screenId);
        
        // Se há uma seleção local para esta tela, usar apenas ela (mudanças explícitas)
        if (localSelection !== undefined) {
            return localSelection.has(productId);
        }
        
        // Se não há seleção local, usar o estado da API (produto já estava vinculado)
        return produtosNaAPI.has(productId);
    }, [telasOtimizadas, localSelections, promoIds]);

    // Calcular quantos produtos novos serão adicionados/desvinculados para uma tela
    // MODO LINK: produtos marcados que não estão na API (novos)
    // MODO UNLINK: produtos marcados que estão na API (para desvincular)
    // IMPORTANTE: Usar apenas localSelections como fonte de verdade, não depender de isSelected
    const getNovosProdutosCount = useCallback((screenId: number): number => {
        // Buscar tela otimizada
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        // Obter seleção local (mudanças desta sessão) - FONTE ÚNICA DE VERDADE
        const localSelection = localSelections.get(screenId);
        
        // Se há seleção local, contar produtos marcados
        if (localSelection !== undefined && localSelection.size > 0) {
            let count = 0;
            localSelection.forEach(productId => {
                if (promoIds.includes(productId)) {
                    if (mode === 'link' && !produtosNaAPI.has(productId)) {
                        // MODO LINK: contar produtos novos (não estão na API)
                        count++;
                    } else if (mode === 'unlink' && produtosNaAPI.has(productId)) {
                        // MODO UNLINK: contar produtos para desvincular (estão na API)
                        count++;
                    }
                }
            });
            return count;
        }
        
        // Se não há seleção local ou está vazia, retornar 0
        return 0;
    }, [telasOtimizadas, localSelections, promoIds, localSelectionsVersion, mode]);

    // Verificar se a tela tem produtos dos promoIds (mesmo que não esteja marcada)
    const hasPromoIdsInScreen = useCallback((screenId: number): boolean => {
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        // Verificar se algum produto dos promoIds está na API
        return promoIds.some(id => produtosNaAPI.has(id));
    }, [telasOtimizadas, promoIds]);


    // Toggle para marcar/desmarcar produto em uma tela
    // MODO LINK: Só permite marcar/desmarcar produtos NOVOS (que não estão na API)
    // MODO UNLINK: Só permite marcar/desmarcar produtos que JÁ ESTÃO na API (vinculados)
    const toggleProductForScreen = useCallback((screenId: number, productId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevenir que o clique selecione a tela
        
        // Obter estado atual da API para esta tela (usar dados otimizados)
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        // MODO LINK: Só permite marcar produtos novos (que não estão na API)
        if (mode === 'link') {
            // Se o produto já está na API, não permitir desmarcar (não fazer nada)
            if (produtosNaAPI.has(productId)) {
                return; // Produto já está na tela, não pode ser desmarcado
            }
        } else {
            // MODO UNLINK: Só permite marcar produtos que já estão na API
            if (!produtosNaAPI.has(productId)) {
                return; // Produto não está na tela, não pode ser desvinculado
            }
        }
        
        // Só produtos válidos podem ser marcados/desmarcados
        setLocalSelections(prev => {
            const newMap = new Map(prev);
            let screenSelection = newMap.get(screenId);
            
            // Se não há seleção local ainda, inicializar vazio (só produtos novos serão adicionados)
            if (!screenSelection) {
                screenSelection = new Set<number>();
            }
            
            const newSelection = new Set(screenSelection);
            
            // Toggle: se está marcado, desmarcar; se não está, marcar
            if (newSelection.has(productId)) {
                    // Limpar erro de validação se existir
                    setValidationErrors(prev => {
                        const newErrors = new Map(prev);
                        newErrors.delete(screenId);
                        return newErrors;
                    });
                
                newSelection.delete(productId);
            } else {
                // Limpar erro de validação ao marcar
                setValidationErrors(prev => {
                    const newErrors = new Map(prev);
                    newErrors.delete(screenId);
                    return newErrors;
                });
                
                newSelection.add(productId);
            }
            
            // Sempre manter a seleção local (mesmo que vazia) para indicar que houve interação
            newMap.set(screenId, newSelection);
            
            return newMap;
        });
        
            // Forçar recálculo do hasChangesForScreen após atualizar localSelections
        setLocalSelectionsVersion((prev: number) => prev + 1);
    }, [telasOtimizadas, localSelections, promoIds, mode]);

    // Toggle para expandir/recolher produtos vinculados - com lazy loading
    const toggleProductsExpanded = useCallback((screenId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevenir que o clique no botão selecione a tela
        setExpandedScreens(prev => {
            const newSet = new Set(prev);
            const isExpanding = !newSet.has(screenId);
            
            if (isExpanding) {
                // Ao expandir, carregar produtos se ainda não foram carregados
                newSet.add(screenId);
                loadScreenProducts(screenId);
            } else {
                // Ao recolher, apenas remover do set
                newSet.delete(screenId);
            }
            return newSet;
        });
    }, [loadScreenProducts]);

    // Toggle para expandir/recolher produtos que podem ser vinculados
    const toggleAvailableProducts = useCallback((screenId: number, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevenir que o clique no botão selecione a tela
        setExpandedAvailableProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(screenId)) {
                newSet.delete(screenId);
            } else {
                newSet.add(screenId);
            }
            return newSet;
        });
    }, []);

    // Processar e salvar uma tela específica
    const processAndSaveScreen = async (screenId: number): Promise<{ success: boolean; screenName: string; error?: string }> => {
        const screen = screensToUse.find(s => s.id === screenId);
        if (!screen) {
            return { success: false, screenName: `Tela ${screenId}`, error: 'Tela não encontrada' };
        }

        // Verificar se há erro de validação para esta tela
        const validationError = validationErrors.get(screenId);
        if (validationError) {
            return { success: false, screenName: screen.name, error: validationError };
        }
        
        // 1. PRIMEIRO: Obter produtos que JÁ ESTÃO na API (manter esses como base) - OTIMIZADO
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            // produtoIds já é array de números nos dados otimizados
            for (let i = 0; i < tela.produtoIds.length; i++) {
                const produtoId = tela.produtoIds[i];
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            }
        }
        
        // 2. SEGUNDO: Obter seleções locais (mudanças feitas nesta sessão)
        const localSelection = localSelections.get(screenId);
        
        // MODO UNLINK: Processar produtos para desvincular
        if (mode === 'unlink') {
            // No modo unlink, processar apenas produtos que estão marcados para desvincular
            let produtosParaDesvincular = new Set<number>();
            
            if (localSelection !== undefined && localSelection.size > 0) {
                // Calcular produtos para desvincular: estão marcados no localSelection E estão na API
                localSelection.forEach(productId => {
                    if (promoIds.includes(productId) && produtosNaAPI.has(productId)) {
                        // Produto está marcado e está na API = precisa ser desvinculado
                        produtosParaDesvincular.add(productId);
                    }
                });
            }
            
            if (produtosParaDesvincular.size === 0) {
                return { success: false, screenName: screen.name, error: 'Nenhum produto selecionado para desvincular' };
            }
            
            const produtoIdsToDesvincular = Array.from(produtosParaDesvincular).sort((a, b) => a - b);
            
            try {
                if (!screen.id || screen.id <= 0) {
                    throw new Error('ID da tela inválido');
                }
                
                const sucesso = await bannerService.desvincularProdutos(screen.id, produtoIdsToDesvincular);
                
                if (sucesso) {
                    return { success: true, screenName: screen.name };
                } else {
                    throw new Error('A API retornou false ao desvincular produtos');
                }
            } catch (error) {
                let errorMessage = 'Erro desconhecido';
                if (error instanceof Error) {
                    errorMessage = error.message;
                }
                return { success: false, screenName: screen.name, error: errorMessage };
            }
        }
        
        // MODO LINK: Processar produtos para vincular (código original)
        // 3. TERCEIRO: Construir lista de produtos NOVOS (que não estão na API mas devem estar)
        // OTIMIZAÇÃO: Enviar APENAS produtos que precisam ser ADICIONADOS
        
        let produtosNovosParaAdicionar = new Set<number>();
        
        if (localSelection !== undefined) {
            // Usuário interagiu - processar apenas produtos NOVOS
            // Calcular produtos NOVOS: estão marcados no localSelection mas NÃO estão na API
            localSelection.forEach(productId => {
                if (promoIds.includes(productId) && !produtosNaAPI.has(productId)) {
                    // Produto está marcado mas não está na API = precisa ser adicionado
                    produtosNovosParaAdicionar.add(productId);
                }
            });
        }
        
        // OTIMIZAÇÃO: Enviar APENAS produtos NOVOS (que não estão na API)
        // A API fará merge: adicionará os produtos novos aos existentes
        let produtoIdsToSend: number[] = [];
        
        if (produtosNovosParaAdicionar.size > 0) {
            // Apenas produtos novos - enviar apenas eles (API fará merge) preservando ordem de seleção
            produtoIdsToSend = Array.from(produtosNovosParaAdicionar);
            
        } else {
            // Nenhuma mudança - não enviar nada (ou enviar array vazio se necessário)
            produtoIdsToSend = [];
            console.log('⚠️ Nenhum produto novo para adicionar na tela', screen.name);
        }
        
        // Calcular mudanças para o log
        const produtosQueEstavamNaAPI = promoIds.filter(id => produtosNaAPI.has(id));
        const produtosMarcadosPeloUsuario = localSelection !== undefined 
            ? promoIds.filter(id => localSelection.has(id))
            : produtosQueEstavamNaAPI;
        
        try {
            // Validações antes de enviar
            if (!screen.id || screen.id <= 0) {
                throw new Error('ID da tela inválido');
            }

            // Garantir que produtoIds seja sempre um array válido de números
            // OTIMIZADO: Pré-filtrar com tamanho conhecido
            const produtoIdsLength = produtoIdsToSend.length;
            const produtoIdsToSendFiltered: number[] = [];
            for (let p = 0; p < produtoIdsLength; p++) {
                const id = produtoIdsToSend[p];
                if (typeof id === 'number' && id > 0 && Number.isInteger(id)) {
                    produtoIdsToSendFiltered.push(id);
                }
            }
            
            // Usar array filtrado
            const produtoIdsFinal = produtoIdsToSendFiltered;

            // NOVA FUNÇÃO SQL: Não precisa buscar configurações da API
            // A função SQL só atualiza configurações se fornecidas explicitamente
            // Usar dados do cache (tela) para nome e tipo
            if (!tela) {
                throw new Error(`Tela com ID ${screen.id} não encontrada no cache`);
            }

            // Construir comando apenas com campos obrigatórios para atualização de produtos
            // id, nome, tipo, produtoIds - SEM configurações
            // Validar nome: usar screen.name se existir e não for vazio, senão usar tela.nome
            const nomeParaEnviar = (screen.name && screen.name.trim()) || (tela.nome && tela.nome.trim());
            if (!nomeParaEnviar) {
                throw new Error('Nome da tela é obrigatório e não pode estar vazio');
            }

            // Validar tipo: deve ser um número válido
            const tipoParaEnviar = tela.tipo ?? 0;
            if (typeof tipoParaEnviar !== 'number' || tipoParaEnviar < 0) {
                throw new Error('Tipo da tela deve ser um número válido');
            }

            const command: AlterarTelaBannerCommand & { produtoIds?: number[] } = {
                id: screen.id,
                nome: nomeParaEnviar, // Nome obrigatório (validado)
                tipo: tipoParaEnviar, // Tipo obrigatório (validado)
                produtoIds: produtoIdsFinal // OTIMIZADO: Apenas produtos novos (que não estão na API) ou lista completa se houver remoções
                // NÃO incluir configuracoes - a nova função SQL não atualiza se não for fornecido
            };

            // Validação final do comando antes de enviar
            if (!command.id || command.id <= 0) {
                throw new Error('ID da tela inválido no payload');
            }
            if (!command.nome || command.nome.trim() === '') {
                throw new Error('Nome da tela não pode estar vazio');
            }
            if (typeof command.tipo !== 'number' || command.tipo < 0) {
                throw new Error('Tipo da tela deve ser um número válido');
            }
            if (command.produtoIds !== undefined && !Array.isArray(command.produtoIds)) {
                throw new Error('produtoIds deve ser um array');
            }

            // Limpar campos undefined (mas manter todos os obrigatórios)
            const cleanedCommand: any = {
                id: command.id,
                nome: command.nome,
                tipo: command.tipo
            };

            // Sempre incluir produtoIds (mesmo que vazio, se foi especificado)
            if (command.produtoIds !== undefined) {
                cleanedCommand.produtoIds = command.produtoIds;
            }

            // NÃO incluir configuracoes - a nova função SQL preserva as existentes se não for fornecido

            // VALIDAÇÃO CRÍTICA: Garantir que NÃO estamos enviando produtos que já estão na API
            if (cleanedCommand.produtoIds && cleanedCommand.produtoIds.length > 0) {
                const produtosQueJaEstaoNaAPI = cleanedCommand.produtoIds.filter((id: number) => produtosNaAPI.has(id));
                if (produtosQueJaEstaoNaAPI.length > 0) {
                    throw new Error(`Tentando enviar produtos que já estão na API: ${produtosQueJaEstaoNaAPI.join(', ')}`);
                }
            }

            // Enviar apenas campos obrigatórios: id, nome, tipo, produtoIds
            const sucesso = await bannerService.atualizarTela(cleanedCommand);
            
            if (sucesso) {
                return { success: true, screenName: screen.name };
            } else {
                throw new Error('A API retornou false ao atualizar a tela');
            }
        } catch (error) {
            
            // Extrair mensagem de erro mais detalhada
            let errorMessage = 'Erro desconhecido';
            if (error instanceof Error) {
                errorMessage = error.message;
                
                // Se for erro 400, verificar se é por causa de array vazio
                if (errorMessage.includes('400') || errorMessage.includes('Erro 400')) {
                    // Verificar se não há produtos novos para adicionar
                    if (produtoIdsToSend.length === 0 && produtosNovosParaAdicionar.size === 0) {
                        errorMessage = 'Nenhum produto novo para adicionar';
                    }
                }
            }
            
            return { success: false, screenName: screen.name, error: errorMessage };
        }
    };

    // Salvar produtos vinculados na API para todas as telas com alterações
    // PUT /v1/banner/tela com body: { id, nome, tipo, produtoIds }
    // NOTA: Não enviamos configuracoes - a nova função SQL preserva as existentes se não for fornecido
    const handleSaveToAPI = async () => {
        const screensToSave = getScreensWithChanges;
        
        if (screensToSave.length === 0) {
            return;
        }

        // Verificar se há erros de validação em alguma tela que será salva
        const screensWithErrors = screensToSave.filter(screenId => validationErrors.has(screenId));
        if (screensWithErrors.length > 0) {
            return;
        }

        setSaving(true);
        const results: Array<{ success: boolean; screenName: string; error?: string }> = [];
        
        // Rastrear estado ANTES de salvar para calcular mudanças finais
        // Map<productId, Set<screenId>> - telas onde cada produto estava vinculado antes
        const produtosVinculadosAntes = new Map<number, Set<number>>();
        
        // Rastrear estado DEPOIS de salvar para calcular mudanças finais
        // Map<productId, Set<screenId>> - telas onde cada produto está vinculado depois
        const produtosVinculadosDepois = new Map<number, Set<number>>();
        
        // Inicializar mapas com estado atual da API para todos os promoIds
        promoIds.forEach(promoId => {
            produtosVinculadosAntes.set(promoId, new Set<number>());
            produtosVinculadosDepois.set(promoId, new Set<number>());
        });
        
        // Preencher estado ANTES: verificar em quais telas cada produto estava vinculado
        promoIds.forEach(promoId => {
            telasOtimizadas.forEach(tela => {
                if (tela.produtoIds && Array.isArray(tela.produtoIds)) {
                    if (tela.produtoIds.includes(promoId)) {
                        const telasAntes = produtosVinculadosAntes.get(promoId);
                        if (telasAntes) {
                            telasAntes.add(tela.id);
                        }
                    }
                }
            });
        });
        
        // Criar mapa de telas modificadas ANTES de processar (para preservar localSelections)
        const screensModifiedTemp = new Map<number, number[]>();
        
        // Calcular produtos finais de cada tela antes de salvar
        screensToSave.forEach(screenId => {
            const tela = telasOtimizadas.find(t => t.id === screenId);
            const produtosNaAPIAntes = new Set<number>();
            
            if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                tela.produtoIds.forEach((produtoId: number) => {
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        produtosNaAPIAntes.add(produtoId);
                    }
                });
            }
            
            const localSelection = localSelections.get(screenId);
            const produtosFinais = new Set<number>(produtosNaAPIAntes);
            
            if (mode === 'link') {
                // MODO LINK: Adicionar produtos selecionados
                if (localSelection) {
                    localSelection.forEach(produtoId => {
                        produtosFinais.add(produtoId);
                    });
                }
            } else {
                // MODO UNLINK: Remover produtos selecionados
                if (localSelection) {
                    localSelection.forEach(produtoId => {
                        produtosFinais.delete(produtoId);
                    });
                }
            }
            
            // Preservar ordem de inserção (sem ordenar numericamente)
            screensModifiedTemp.set(screenId, Array.from(produtosFinais));
        });
        
        // Mapa final com apenas telas salvas com sucesso
        const screensModified = new Map<number, number[]>();
        
        try {
            // Processar cada tela sequencialmente
            for (const screenId of screensToSave) {
                // Obter estado antes de salvar para calcular mudanças
                const tela = telasOtimizadas.find(t => t.id === screenId);
                const produtosNaAPIAntes = new Set<number>();
                
                if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                    tela.produtoIds.forEach(produtoId => {
                        if (typeof produtoId === 'number' && produtoId > 0) {
                            produtosNaAPIAntes.add(produtoId);
                        }
                    });
                }
                
                const result = await processAndSaveScreen(screenId);
                results.push(result);
                
                if (result.success) {
                    // Adicionar ao mapa final de telas modificadas (apenas se salvou com sucesso)
                    const produtosFinais = screensModifiedTemp.get(screenId);
                    if (produtosFinais) {
                        screensModified.set(screenId, produtosFinais);
                    }
                    
                    // Calcular estado DEPOIS: verificar seleção local para esta tela
                    const localSelection = localSelections.get(screenId);
                    
                    // Para cada produto dos promoIds, verificar se está vinculado nesta tela após salvar
                    promoIds.forEach(promoId => {
                        const telasDepois = produtosVinculadosDepois.get(promoId);
                        if (telasDepois) {
                            // IMPORTANTE: A lógica é diferente para modo 'link' e 'unlink'
                            let estaVinculado: boolean;
                            
                            if (mode === 'unlink') {
                                // MODO UNLINK: Se há seleção local e o produto está marcado,
                                // significa que ele foi DESVINCULADO (não está mais vinculado)
                                // Se não há seleção local, usar estado da API antes (produto permaneceu como estava)
                                if (localSelection !== undefined) {
                                    // Se o produto está marcado no localSelection, ele foi desvinculado
                                    // Então: estava na API antes E não está marcado no localSelection = ainda vinculado
                                    // OU: estava na API antes E está marcado no localSelection = foi desvinculado (não está mais)
                                    estaVinculado = produtosNaAPIAntes.has(promoId) && !localSelection.has(promoId);
                                } else {
                                    // Sem seleção local, manter estado da API (produto não foi modificado)
                                    estaVinculado = produtosNaAPIAntes.has(promoId);
                                }
                            } else {
                                // MODO LINK: Se há seleção local, usar ela; senão, usar estado da API
                                estaVinculado = localSelection !== undefined
                                    ? localSelection.has(promoId)
                                    : produtosNaAPIAntes.has(promoId);
                            }
                            
                            if (estaVinculado) {
                                telasDepois.add(screenId);
                            }
                        }
                    });
                    
                    // Limpar estado local para a tela que foi salva
                    setLocalSelections(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(screenId);
                        return newMap;
                    });
                } else {
                    // Se falhou, não adicionar ao mapa de telas modificadas
                    // Se falhou, manter estado da API para esta tela
                    promoIds.forEach(promoId => {
                        const telasDepois = produtosVinculadosDepois.get(promoId);
                        if (telasDepois && produtosNaAPIAntes.has(promoId)) {
                            telasDepois.add(screenId);
                        }
                    });
                }
            }
            
            // Para telas que NÃO foram modificadas, manter estado da API
            telasOtimizadas.forEach(tela => {
                if (!screensToSave.includes(tela.id)) {
                    if (tela.produtoIds && Array.isArray(tela.produtoIds)) {
                        promoIds.forEach(promoId => {
                            if (tela.produtoIds!.includes(promoId)) {
                                const telasDepois = produtosVinculadosDepois.get(promoId);
                                if (telasDepois) {
                                    telasDepois.add(tela.id);
                                }
                            }
                        });
                    }
                }
            });
            
            // Calcular estado final: produtos vinculados ou desvinculados dependendo do modo
            if (mode === 'unlink') {
                // MODO UNLINK: Calcular produtos desvinculados
                // IMPORTANTE: Um produto só deve ser marcado como "não vinculado" quando ele foi
                // desvinculado de TODAS as telas. Se ainda está vinculado a pelo menos uma tela,
                // ele continua como "vinculado"
                const unlinkedProductIds = new Set<number>();
                
                promoIds.forEach(promoId => {
                    const telasAntes = produtosVinculadosAntes.get(promoId) || new Set();
                    const telasDepois = produtosVinculadosDepois.get(promoId) || new Set();
                    
                    const estavaVinculado = telasAntes.size > 0;
                    const estaVinculado = telasDepois.size > 0;
                    
                    // IMPORTANTE: Só marcar como "não vinculado" se o produto estava vinculado antes
                    // E agora não está vinculado a NENHUMA tela (desvinculado de todas as telas)
                    if (estavaVinculado && !estaVinculado) {
                        unlinkedProductIds.add(promoId);
                    }
                    // Se o produto ainda está vinculado a pelo menos uma tela (estaVinculado === true),
                    // ele NÃO deve ser marcado como "não vinculado", mesmo que tenha sido desvinculado de algumas telas
                });
                
                // Chamar callback externo com IDs desvinculados e telas modificadas
                // Este callback atualiza o status local do produto de "vinculado" para "não vinculado"
                if (onSaveToAPI) {
                    try {
                        await onSaveToAPI(
                            [], // Não vinculamos produtos no modo unlink
                            Array.from(unlinkedProductIds),
                            screensModified
                        );
                    } catch (callbackError) {
                        // Erro não crítico, continuar
                    }
                }
            } else {
                // MODO LINK: Calcular produtos vinculados
                // Um produto está vinculado se está vinculado em PELO MENOS UMA tela
                const linkedProductIds = new Set<number>();
                
                promoIds.forEach(promoId => {
                    const telasAntes = produtosVinculadosAntes.get(promoId) || new Set();
                    const telasDepois = produtosVinculadosDepois.get(promoId) || new Set();
                    
                    const estavaVinculado = telasAntes.size > 0;
                    const estaVinculado = telasDepois.size > 0;
                    
                    // Se não estava vinculado e agora está, foi vinculado
                    if (!estavaVinculado && estaVinculado) {
                        linkedProductIds.add(promoId);
                    }
                    // Se estava vinculado e agora está em mais telas, também adicionar aos vinculados
                    // (para garantir que o cache reflita corretamente)
                    else if (estavaVinculado && estaVinculado && telasDepois.size > telasAntes.size) {
                        linkedProductIds.add(promoId);
                    }
                    // Se estava vinculado e ainda está nas mesmas telas, não precisa fazer nada
                    // (o cache já reflete corretamente)
                });
                
                // Chamar callback externo com IDs vinculados e telas modificadas
                if (onSaveToAPI) {
                    try {
                        await onSaveToAPI(
                            Array.from(linkedProductIds),
                            [], // Não desvinculamos produtos no modo link
                            screensModified
                        );
                    } catch (callbackError) {
                        // Erro não crítico, continuar
                    }
                }
            }
            
            // NÃO recarregar telas aqui - os dados serão atualizados na próxima vez que as telas
            // forem carregadas naturalmente (ex: ao navegar para página de telas ou recarregar)
            // Isso evita que mudanças em loadingTelas disparem buscas desnecessárias
            
            // Fechar modal se todas as telas foram salvas com sucesso
            const failCount = results.filter(r => !r.success).length;
            if (failCount === 0) {
                onClose();
            }
        } catch (error) {
            // Erro geral - não fazer nada, apenas não fechar o modal
        } finally {
            setSaving(false);
        }
    };

    // Verificar se há alterações para salvar em uma tela específica
    const hasChangesForScreen = useMemo(() => {
        if (!selectedScreenId) {
            return false;
        }
        
        const tela = telasOtimizadas.find(t => t.id === selectedScreenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        const localSelection = localSelections.get(selectedScreenId);
        
        // Se não há seleção local, não há alterações (usuário não interagiu)
        if (localSelection === undefined) {
            return false;
        }
        
        // Verificar se há alterações baseado no modo
        if (mode === 'unlink') {
            // MODO UNLINK: Verificar se há produtos marcados que estão na API (para desvincular)
            for (const productId of promoIds) {
                const isInLocal = localSelection.has(productId);
                const isInAPI = produtosNaAPI.has(productId);
                
                // Se está marcado no localSelection e está na API, há alteração (será desvinculado)
                if (isInLocal && isInAPI) {
                    return true;
                }
            }
        } else {
            // MODO LINK: Verificar se há diferença entre o estado local e o estado da API
            for (const productId of promoIds) {
                const isInLocal = localSelection.has(productId);
                const isInAPI = produtosNaAPI.has(productId);
                
                // Se há diferença, há alterações
                if (isInLocal !== isInAPI) {
                    return true;
                }
            }
        }
        
        return false; // Não há diferenças
    }, [selectedScreenId, localSelectionsVersion, localSelections, telasOtimizadas, promoIds.join(','), mode]);

    // Obter todas as telas que têm alterações para salvar
    const getScreensWithChanges = useMemo(() => {
        const screensWithChanges: number[] = [];
        
        screensToUse.forEach(screen => {
            // IMPORTANTE: Usar dados otimizados diretamente da API (telasOtimizadas) para garantir dados atualizados
            const tela = telasOtimizadas.find(t => t.id === screen.id);
            const produtosNaAPI = new Set<number>();
            
            if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                tela.produtoIds.forEach(produtoId => {
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        produtosNaAPI.add(produtoId);
                    }
                });
            }
            
            const localSelection = localSelections.get(screen.id);
            
            // Se não há seleção local, não há alterações
            if (localSelection === undefined) {
                return;
            }
            
            // Verificar se há produtos marcados para vincular/desvincular
            if (mode === 'unlink') {
                // MODO UNLINK: Verificar se há produtos marcados que estão na API (para desvincular)
                for (const productId of promoIds) {
                    // Só considerar produtos que estão na API
                    if (produtosNaAPI.has(productId)) {
                        const isInLocal = localSelection.has(productId);
                        
                        // Se o produto vinculado está marcado para desvincular, há alterações
                        if (isInLocal) {
                            screensWithChanges.push(screen.id);
                            return; // Já identificou que tem alterações, pode parar
                        }
                    }
                }
            } else {
                // MODO LINK: Verificar se há produtos NOVOS marcados (que não estão na API)
                for (const productId of promoIds) {
                    // Só considerar produtos novos (que não estão na API)
                    if (!produtosNaAPI.has(productId)) {
                        const isInLocal = localSelection.has(productId);
                        
                        // Se o produto novo está marcado, há alterações
                        if (isInLocal) {
                            screensWithChanges.push(screen.id);
                            return; // Já identificou que tem alterações, pode parar
                        }
                    }
                }
            }
        });
        
        return screensWithChanges;
    }, [screensToUse, telasOtimizadas, localSelections, localSelectionsVersion, promoIds.join(','), mode]);

    // Verificar se há alguma alteração em qualquer tela
    const hasAnyChanges = useMemo(() => {
        return getScreensWithChanges.length > 0;
    }, [getScreensWithChanges]);

    const handleScreenClick = (screenId: number, e?: React.MouseEvent) => {
        // Prevenir propagação se o evento foi passado (clique direto no card)
        if (e) {
            e.stopPropagation();
        }
        
        // Obter estado atual da API para esta tela (usar dados otimizados)
        const tela = telasOtimizadas.find(t => t.id === screenId);
        const produtosNaAPI = new Set<number>();
        
        if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
            tela.produtoIds.forEach(produtoId => {
                if (typeof produtoId === 'number' && produtoId > 0) {
                    produtosNaAPI.add(produtoId);
                }
            });
        }
        
        // MODO UNLINK: Lógica diferente - marcar/desmarcar produtos vinculados
        if (mode === 'unlink') {
            const produtosVinculados = promoIds.filter(id => produtosNaAPI.has(id));
            const localSelectionAtual = localSelections.get(screenId);
            const produtosMarcadosParaDesvincular = localSelectionAtual 
                ? produtosVinculados.filter(id => localSelectionAtual.has(id))
                : [];
            
            // Se não há produtos vinculados, não fazer nada
            if (produtosVinculados.length === 0) {
                if (selectedScreenId !== screenId) {
                    setSelectedScreenId(screenId);
                }
                return;
            }
            
            // Toggle: se todos estão marcados, desmarcar todos; senão, marcar todos
            setLocalSelections(prev => {
                const newMap = new Map(prev);
                const todosMarcados = produtosVinculados.length > 0 && 
                    produtosVinculados.every(id => localSelectionAtual?.has(id));
                
                if (todosMarcados) {
                    // Desmarcar todos - remover seleção local
                    newMap.delete(screenId);
                } else {
                    // Marcar todos os produtos vinculados
                    const newSelection = new Set<number>();
                    produtosVinculados.forEach(id => {
                        newSelection.add(id);
                    });
                    newMap.set(screenId, newSelection);
                }
                
                // Limpar erro de validação se existir
                setValidationErrors(prev => {
                    const newErrors = new Map(prev);
                    newErrors.delete(screenId);
                    return newErrors;
                });
                
                return newMap;
            });
            
            // Atualizar seleção visual
            if (selectedScreenId === screenId) {
                setSelectedScreenId(null);
            } else {
                setSelectedScreenId(screenId);
            }
            
            // Forçar recálculo
            setLocalSelectionsVersion((prev: number) => prev + 1);
            return;
        }
        
        // MODO LINK: Lógica original
        // Verificar se TODOS os produtos selecionados já estão na API
        const todosProdutosJaEstaoNaAPI = promoIds.every(id => produtosNaAPI.has(id));
        
        // Verificar se há produtos novos disponíveis
        const produtosNovosDisponiveis = promoIds.filter(id => !produtosNaAPI.has(id));
        const temProdutosNovos = produtosNovosDisponiveis.length > 0;
        
        // Verificar se há seleção local com produtos novos marcados
        const localSelectionAtual = localSelections.get(screenId);
        const temProdutosNovosMarcados = localSelectionAtual ? 
            produtosNovosDisponiveis.some(id => localSelectionAtual.has(id)) : false;
        
        // Se todos os produtos já estão na tela E não há produtos novos marcados, não fazer nada
        if (todosProdutosJaEstaoNaAPI && !temProdutosNovosMarcados) {
            // Apenas atualizar a seleção visual (se necessário)
            if (selectedScreenId !== screenId) {
                setSelectedScreenId(screenId);
            }
            return; // Não fazer alterações na seleção
        }
        
        // Se todos os produtos estão na tela mas há produtos novos marcados, permitir desmarcar
        // (isso não deveria acontecer, mas vamos tratar para garantir)
        if (todosProdutosJaEstaoNaAPI && temProdutosNovosMarcados) {
            // Desmarcar produtos novos marcados
            setLocalSelections(prev => {
                const newMap = new Map(prev);
                newMap.delete(screenId);
                return newMap;
            });
            // Forçar atualização visual
            setLocalSelectionsVersion((prev: number) => prev + 1);
            return;
        }
        
        // Se há produtos novos para adicionar, permitir marcar/desmarcar
        // IMPORTANTE: Atualizar localSelections primeiro para garantir que o estado seja atualizado corretamente
        setLocalSelections(prev => {
            const newMap = new Map(prev);
            let screenSelection = newMap.get(screenId);
            
            // Calcular produtos novos disponíveis uma vez
            const produtosNovos = promoIds.filter(id => !produtosNaAPI.has(id));
            
            // Se não há seleção local ainda OU a seleção não tem produtos novos, inicializar com produtos novos
            if (!screenSelection || screenSelection.size === 0 || 
                !produtosNovos.some(id => screenSelection!.has(id))) {
                screenSelection = new Set<number>();
                produtosNovos.forEach(id => {
                    screenSelection!.add(id);
                });
                } else {
                // Se já há seleção local com produtos novos, verificar se todos os produtos novos estão marcados
                // IMPORTANTE: Filtrar apenas produtos novos da seleção local (não considerar produtos que já estão na API)
                const produtosNovosNaSelecao = Array.from(screenSelection!).filter(id => 
                    promoIds.includes(id) && !produtosNaAPI.has(id)
                );
                
                // Verificar se todos os produtos novos disponíveis estão marcados
                const todosNovosMarcados = produtosNovos.length > 0 && 
                    produtosNovos.every(id => screenSelection!.has(id));
                
                if (todosNovosMarcados) {
                    // Desmarcar todos os produtos novos - REMOVER completamente a seleção local
                    // Isso permite que a tela volte ao estado inicial (verde se tem produtos, cinza se não tem)
                    newMap.delete(screenId);
                    
                    // Limpar erro de validação se existir
                    setValidationErrors(prev => {
                        const newErrors = new Map(prev);
                        newErrors.delete(screenId);
                        return newErrors;
                    });
                    
                    // IMPORTANTE: Atualizar versão ANTES de retornar para forçar re-renderização visual
                    setLocalSelectionsVersion((prev: number) => prev + 1);
                    
                    return newMap; // Retornar sem atualizar seleção
            } else {
                    // Marcar todos os produtos novos disponíveis
                    produtosNovos.forEach(id => {
                        screenSelection!.add(id);
                    });
                }
            }
            
            // Limpar erro de validação se existir
                setValidationErrors(prev => {
                    const newErrors = new Map(prev);
                    newErrors.delete(screenId);
                    return newErrors;
                });
                
            // Atualizar seleção local
            newMap.set(screenId, screenSelection!);
            
            return newMap;
        });
        
        // Também atualizar a seleção visual da tela
        if (selectedScreenId === screenId) {
            setSelectedScreenId(null);
        } else {
            setSelectedScreenId(screenId);
        }
        
        // Forçar recálculo do hasChangesForScreen após atualizar localSelections
        // IMPORTANTE: Atualizar versão por último para garantir que todos os estados foram atualizados
        setLocalSelectionsVersion((prev: number) => prev + 1);
    };

    // Função para obter nome do layout baseado no tipo da tela
    const getLayoutName = useCallback((screen: Screen): string => {
        const layout = layouts.find(l => l.id === screen.layoutId);
        return layout?.name || 'Sem tipo';
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg max-w-[75%] w-[75%] max-h-[75vh] overflow-hidden flex flex-col">
                <div className="p-3 border-b">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-bold text-gray-800">
                            {mode === 'unlink' 
                                ? (singlePromoId !== null
                                    ? 'Desvincular Produto da Tela'
                                    : `Desvincular ${selectedPromoIds.length} ${selectedPromoIds.length === 1 ? 'produto' : 'produtos'} da Tela`)
                                : (singlePromoId !== null
                                    ? 'Vincular Produto à Tela'
                                    : `Vincular ${selectedPromoIds.length} ${selectedPromoIds.length === 1 ? 'produto' : 'produtos'} à Tela`)
                            }
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {(loadingTelasProp && (!telasOtimizadasProp || telasOtimizadasProp.length === 0)) || (loadingTelas && telasOtimizadas.length === 0) ? (
                        <div className="text-center py-12">
                            <div className="flex flex-col items-center justify-center">
                                <Loader size={32} className="animate-spin text-blue-500 mb-4" />
                                <p className="text-gray-600 text-lg font-medium">Carregando telas...</p>
                                <p className="text-gray-400 text-sm mt-2">Aguarde enquanto buscamos as telas disponíveis</p>
                            </div>
                        </div>
                    ) : errorTelas ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 text-lg mb-2">Erro ao carregar telas</p>
                            <p className="text-gray-400 text-sm mb-4">{errorTelas}</p>
                            <button
                                onClick={() => carregarTelas()}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Tentar novamente
                            </button>
                        </div>
                    ) : screensToUse.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg mb-2">Nenhuma tela encontrada</p>
                            <p className="text-gray-400 text-sm">Crie uma tela no menu "Telas" primeiro</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2 w-full auto-rows-min">
                            {screensToUse.map(screen => {
                                // Verificar quantos produtos dos promoIds estão marcados para esta tela
                                const selectedCount = promoIds.filter(id => 
                                    isProductSelectedForScreen(screen.id, id)
                                ).length;
                                const hasSelected = selectedCount > 0;
                                const screenProducts = getScreenProducts(screen);

                                const isSelected = selectedScreenId === screen.id;
                                
                                // Calcular quantos produtos novos serão adicionados (usar apenas localSelections)
                                const novosProdutosCount = getNovosProdutosCount(screen.id);
                                
                                // Verificar se a tela tem produtos dos promoIds (mesmo que não esteja marcada)
                                const hasPromoIds = hasPromoIdsInScreen(screen.id);
                                
                                // Verificar se TODOS os produtos já estão na tela
                                const tela = telasOtimizadas.find(t => t.id === screen.id);
                                const produtosNaAPI = new Set<number>();
                                if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                                    tela.produtoIds.forEach(produtoId => {
                                        if (typeof produtoId === 'number' && produtoId > 0) {
                                            produtosNaAPI.add(produtoId);
                                        }
                                    });
                                }
                                const todosProdutosJaEstaoNaAPI = promoIds.every(id => produtosNaAPI.has(id));
                                
                                // Verificar se há produtos novos disponíveis para adicionar
                                const produtosNovosDisponiveis = promoIds.filter(id => !produtosNaAPI.has(id));
                                const temProdutosNovos = produtosNovosDisponiveis.length > 0;
                                
                                // MODO UNLINK: Pode clicar se há produtos vinculados
                                // MODO LINK: Só bloquear clique se TODOS os produtos já estão na tela (não há produtos novos)
                                const podeClicar = mode === 'unlink' 
                                    ? hasPromoIds // No modo unlink, pode clicar se há produtos vinculados
                                    : (!todosProdutosJaEstaoNaAPI || temProdutosNovos);
                                
                                // REGRAS DE CORES:
                                // MODO LINK:
                                // 1. Se TODOS os produtos já estão na tela: VERDE (não clicável)
                                // 2. Se há produtos novos marcados: AZUL (clicável, pode desmarcar)
                                // 3. Se há pelo menos 1 produto na tela mas não todos: VERDE quando não marcado, AZUL quando marcado (clicável)
                                // 4. Se não tem nenhum produto: CINZA quando não marcado, AZUL quando marcado (clicável)
                                // MODO UNLINK:
                                // 1. Se há produtos vinculados: VERDE (mostra que tem produtos)
                                // 2. Se há produtos marcados para desvincular: VERMELHO (clicável)
                                const deveFicarVerde = todosProdutosJaEstaoNaAPI || (hasPromoIds && novosProdutosCount === 0);
                                const deveFicarAzul = mode === 'link' && novosProdutosCount > 0;
                                const deveFicarVermelho = mode === 'unlink' && novosProdutosCount > 0;

                                return (
                                    <div
                                        key={screen.id}
                                        onClick={(e) => podeClicar && handleScreenClick(screen.id, e)}
                                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                            !podeClicar
                                                ? 'cursor-default'
                                                : 'cursor-pointer'
                                        } ${
                                            deveFicarVermelho
                                                ? 'border-red-500 bg-red-50 hover:bg-red-100'
                                                : deveFicarAzul
                                                ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                                                : deveFicarVerde
                                                ? 'border-green-500 bg-green-50 hover:bg-green-100'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center mb-1 gap-2">
                                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-800 text-sm truncate">{screen.name}</h3>
                                                <span className="text-xs text-gray-500 flex-shrink-0">•</span>
                                                <p className="text-xs text-gray-500 truncate">{getLayoutName(screen)}</p>
                                            </div>
                                            {novosProdutosCount > 0 && (
                                                <span className={`text-xs text-white px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                                    mode === 'unlink' ? 'bg-red-500' : 'bg-blue-500'
                                                }`}>
                                                    {mode === 'unlink' ? '-' : '+'}{novosProdutosCount}
                                                </span>
                                            )}
                                        </div>

                                        {validationErrors.has(screen.id) && (
                                            <div className="mt-1 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-[10px]">
                                                <p className="text-yellow-800 font-medium">
                                                    {validationErrors.get(screen.id)}
                                                </p>
                                            </div>
                                        )}

                                        {(() => {
                                            const productsCount = getScreenProductsCount(screen);
                                            const screenProducts = getScreenProducts(screen);
                                            const isLoading = loadingProducts.get(screen.id);
                                            
                                            // Obter produtos que podem ser vinculados/desvinculados baseado no modo
                                            const produtosQuePodemSerVinculados = mode === 'link' 
                                                ? promoIds.filter(id => !produtosNaAPI.has(id)) // MODO LINK: produtos novos
                                                : promoIds.filter(id => produtosNaAPI.has(id)); // MODO UNLINK: produtos vinculados
                                            const produtosQuePodemSerVinculadosCount = produtosQuePodemSerVinculados.length;
                                            
                                            // Obter produtos que podem ser vinculados/desvinculados com seus nomes
                                            const produtosQuePodemSerVinculadosComNomes = produtosQuePodemSerVinculados.map(id => {
                                                const promo = promotions.find(p => p.id === id) || storePromotions.find(p => p.id === id);
                                                return { id, name: promo?.name || `Produto ${id}` };
                                            });
                                            
                                                return (
                                                <div className="mt-1.5 space-y-1" key={`products-${screen.id}`}>
                                                    {/* Dropdown: Produtos que podem ser vinculados/desvinculados */}
                                                    {produtosQuePodemSerVinculadosCount > 0 && (
                                                        <button
                                                            onClick={(e) => toggleAvailableProducts(screen.id, e)}
                                                            className={`w-full flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-700 rounded transition-colors cursor-pointer ${
                                                                mode === 'unlink'
                                                                    ? 'bg-red-50 border border-red-300 hover:bg-red-100 hover:border-red-400'
                                                                    : 'bg-blue-50 border border-blue-300 hover:bg-blue-100 hover:border-blue-400'
                                                            }`}
                                                        >
                                                            {expandedAvailableProducts.has(screen.id) ? (
                                                                <ChevronUp size={12} className="text-gray-600" />
                                                            ) : (
                                                                <ChevronDown size={12} className="text-gray-600" />
                                                            )}
                                                            <span>
                                                                {mode === 'unlink' 
                                                                    ? `Produtos que podem ser desvinculados (${produtosQuePodemSerVinculadosCount})`
                                                                    : `Produtos que podem ser vinculados (${produtosQuePodemSerVinculadosCount})`
                                                                }
                                                            </span>
                                                        </button>
                                                    )}
                                                    
                                                    {expandedAvailableProducts.has(screen.id) && produtosQuePodemSerVinculadosCount > 0 && (
                                                        <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                            {produtosQuePodemSerVinculadosComNomes.map(produto => (
                                                                <div
                                                                    key={produto.id}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className={`text-[9px] bg-white px-1.5 py-0.5 rounded border ${
                                                                        mode === 'unlink' 
                                                                            ? 'border-red-200 bg-red-50' 
                                                                            : 'border-blue-200 bg-blue-50'
                                                                    }`}
                                                                >
                                                                    <span className={`font-medium truncate block ${
                                                                        mode === 'unlink' 
                                                                            ? 'text-red-700' 
                                                                            : 'text-blue-700'
                                                                    }`}>
                                                                        {produto.name}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                            
                                            {productsCount > 0 && (
                                                <>
                                                        <button
                                                            onClick={(e) => toggleProductsExpanded(screen.id, e)}
                                                            className="w-full flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded hover:bg-gray-100 hover:border-gray-400 transition-colors cursor-pointer"
                                                            disabled={isLoading}
                                                        >
                                                            {expandedScreens.has(screen.id) ? (
                                                                <ChevronUp size={12} className="text-gray-600" />
                                                            ) : (
                                                                <ChevronDown size={12} className="text-gray-600" />
                                                            )}
                                                            <span>
                                                                Produtos vinculados ({productsCount})
                                                            </span>
                                                            {isLoading && (
                                                                <Loader size={12} className="animate-spin text-gray-500 ml-auto" />
                                                            )}
                                                        </button>
                                                        {expandedScreens.has(screen.id) && (
                                                            <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                                {isLoading ? (
                                                                    <div className="text-center py-2 text-[10px] text-gray-500">
                                                                        <Loader size={12} className="animate-spin mx-auto mb-1" />
                                                                        Carregando...
                                                                    </div>
                                                                ) : screenProducts.length > 0 ? (
                                                                    screenProducts.map(product => {
                                                            const isSelected = isProductSelectedForScreen(screen.id, product.id);
                                                            const isInPromoIds = promoIds.includes(product.id);
                                                            
                                                            // Verificar se o produto já está na API
                                                            const tela = telasOtimizadas.find(t => t.id === screen.id);
                                                            const produtosNaAPI = new Set<number>();
                                                            if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                                                                tela.produtoIds.forEach(produtoId => {
                                                                    if (typeof produtoId === 'number' && produtoId > 0) {
                                                                        produtosNaAPI.add(produtoId);
                                                                    }
                                                                });
                                                            }
                                                            const produtoJaEstaNaAPI = produtosNaAPI.has(product.id);
                                                            
                                                            return (
                                                        <div
                                                            key={product.id}
                                                            onClick={(e) => e.stopPropagation()}
                                                                    className={`text-[9px] bg-white px-1 py-0.5 rounded border transition-colors ${
                                                                        produtoJaEstaNaAPI && isInPromoIds
                                                                            ? 'border-green-500 bg-green-50' 
                                                                            : isSelected 
                                                                            ? (mode === 'unlink' ? 'border-red-500 bg-red-50' : 'border-blue-500 bg-blue-50')
                                                                            : 'border-gray-200'
                                                                    }`}
                                                        >
                                                            <div className="flex justify-between items-center gap-2">
                                                                        <div className="flex items-center gap-1 min-w-0 flex-1">
                                                                            {isInPromoIds && ((mode === 'link' && !produtoJaEstaNaAPI) || (mode === 'unlink' && produtoJaEstaNaAPI)) && (
                                                                                <button
                                                                                    onClick={(e) => toggleProductForScreen(screen.id, product.id, e)}
                                                                                    className={`w-2.5 h-2.5 border-2 rounded flex items-center justify-center transition-colors flex-shrink-0 ${
                                                                                        isSelected
                                                                                            ? (mode === 'unlink' ? 'bg-red-500 border-red-500 text-white' : 'bg-blue-500 border-blue-500 text-white')
                                                                                            : (mode === 'unlink' ? 'border-gray-400 hover:border-red-500' : 'border-gray-400 hover:border-blue-500')
                                                                                    }`}
                                                                                >
                                                                                    {isSelected && <Check size={6} />}
                                                                                </button>
                                                                            )}
                                                                            {isInPromoIds && produtoJaEstaNaAPI && mode === 'link' && (
                                                                                <div className="w-2.5 h-2.5 border-2 border-green-500 bg-green-500 rounded flex items-center justify-center flex-shrink-0">
                                                                                    <Check size={6} className="text-white" />
                                                                                </div>
                                                                            )}
                                                                            <span className={`font-medium truncate text-[9px] ${
                                                                                produtoJaEstaNaAPI && isInPromoIds
                                                                                    ? 'text-green-700' 
                                                                                    : isSelected 
                                                                                    ? (mode === 'unlink' ? 'text-red-700' : 'text-blue-700')
                                                                                    : 'text-gray-700'
                                                                            }`}>
                                                                    {product.name}
                                                                                {produtoJaEstaNaAPI && isInPromoIds && (
                                                                                    <span className="ml-0.5 text-[8px] text-green-600">(já vinculado)</span>
                                                                                )}
                                                                </span>
                                                                        </div>
                                                                <div className="flex items-center gap-0.5 text-gray-600 flex-shrink-0">
                                                                    <span className="font-semibold text-green-600 text-[9px]">
                                                                        {formatPrice(product.price)}
                                                                    </span>
                                                                    <span className="text-gray-400 text-[8px]">/</span>
                                                                    <span className="text-[8px]">{product.unit}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                            );
                                                        })
                                                                ) : (
                                                                    <div className="text-center py-2 text-[10px] text-gray-500">
                                                                        Nenhum produto encontrado
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                        )}
                                                    </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="p-2 border-t bg-gray-50 space-y-1.5">
                        {getScreensWithChanges.length > 0 && (
                            <div className="p-1.5 bg-blue-50 border border-blue-200 rounded text-[10px]">
                                <p className="text-blue-800 font-medium text-center">
                                    {getScreensWithChanges.length} {getScreensWithChanges.length === 1 ? 'tela com alterações' : 'telas com alterações'} para salvar
                                </p>
                            </div>
                        )}
                        {getScreensWithChanges.some(id => validationErrors.has(id)) && (
                            <div className="p-1.5 bg-yellow-50 border border-yellow-200 rounded text-[10px]">
                                <p className="text-yellow-800 font-medium text-center">
                                    Algumas telas têm erros de validação. Corrija antes de salvar.
                                </p>
                            </div>
                        )}
                        <button
                            onClick={handleSaveToAPI}
                            disabled={saving || !hasAnyChanges || getScreensWithChanges.some(id => validationErrors.has(id))}
                            className={`w-full px-3 py-1.5 rounded-lg text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                                mode === 'unlink' 
                                    ? 'bg-red-500 hover:bg-red-600' 
                                    : 'bg-blue-500 hover:bg-blue-600'
                            }`}
                        >
                            {saving ? (
                                <>
                                    <Loader size={14} className="animate-spin" />
                                    {mode === 'unlink' ? 'Desvinculando...' : 'Salvando...'}
                                </>
                            ) : (
                                <>
                                    <Save size={14} />
                                    {mode === 'unlink' ? 'Desvincular produtos' : 'Salvar alterações'}
                                </>
                            )}
                        </button>
                    <button
                        onClick={onClose}
                        className="w-full px-3 py-1.5 bg-white text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};
